import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import Grid from '@material-ui/core/Grid';
import { FormHandles } from '@unform/core';
import { Form as UForm } from '@unform/web';
import * as Yup from 'yup';
import AsyncSelect from 'react-select/async';
import { format, isAfter } from 'date-fns';
import getValidationError from '../../utils/getValidationsErros';
import customStyle from './customStyle';

/** hooks */
import { useHttp } from '../../hooks/http';
import { useAuth } from '../../hooks/auth';

/** Componentes */
import Layout from '../../components/Layout';
import Drawer from '../../components/Drawer';
import HeaderFilterDrawer from '../../components/Headers/FilterDrawer';
import Pagination from '../../components/Pagination';
import Card from './Card';
import {
  WrapperLabel, Input, DatePicker, Select, Button, ButtonIcon,
} from '../../components/Forms';

import {
  ArrowBarRightIcon,
  ListIcon, TrashIcon,
} from '../../styles/icons';
import { Container, DrawerContent } from './styles';

/** interfaces */
import { IClosingForm, IPaginate } from '../../interfaces';

interface IOptions {
  label: string;
  value: string;
}

interface IFilter {
  id: string;
  startDate: Date;
  endDate: Date;
  register_number: string;
  upajs: number[];
  endSituations: number[];
  management_id: number;
  user_id: string;
  description: string;
}

const breadcrumbs = {
  icon: ListIcon,
  links: [
    { title: 'Formulário de encerramento', path: '' },
  ],
};

const ClosingForms: React.FC = () => {
  const { httpGet, loading } = useHttp();
  const { iCan } = useAuth();
  const formRef = useRef<FormHandles>(null);
  const RefLabelToggle = useRef<HTMLLabelElement>(null);
  const [closingForms, setClosingForms] = useState<IClosingForm[]>([]);

  const [endSituations, setEndSituations] = useState([]);

  const [filter, setFilter] = useState<IFilter>({} as IFilter);
  const [upajs, setUpajs] = useState<IOptions[] | null>(null);
  const [user, setUser] = useState<IOptions | null>(null);
  const [managements, setManagements] = useState<IOptions[] | null>(null);
  const [paginate, setPaginate] = React.useState<IPaginate>({ page: 1, lastPage: 1, perPage: 10 });

  const getOrders = useCallback(async (filter: IFilter, paginate: IPaginate) => {
    const { status, data } = await httpGet('closing-forms', {
      params: { ...filter, ...paginate },
    });

    if (status === 200) {
      setClosingForms(data.data);
      setPaginate(data.pagination);
    }
  }, [httpGet]);

  useEffect(() => {
    (async () => {
      const [resManagements, resEndSituations] = await Promise.all([
        httpGet('/managements/all', undefined, false),
        httpGet('/end-situations/all', undefined, false),
      ]);

      if (resManagements.data) {
        const managementsParsed = resManagements.data
          .map((management: any) => ({ value: management.id, label: management.initials }));
        setManagements(managementsParsed);
      }

      if (resEndSituations.data) {
        const endSituationParsed = resEndSituations.data
          .map((endSituation: any) => ({ value: endSituation.id, label: endSituation.name }));
        setEndSituations(endSituationParsed);
      }
    })();

    getOrders(
      { id: '' } as IFilter,
      { page: 1, lastPage: 1, perPage: 10 },
    );
  }, [getOrders, httpGet]);

  const handleSearch = useCallback((value: string) => {
    setPaginate((oldPaginate: any) => ({ ...oldPaginate, page: 1 }));
    setFilter((oldState: any) => ({ ...oldState, id: value }));

    getOrders(
      { id: value } as IFilter,
      { page: 1, lastPage: 1, perPage: 10 },
    );
  }, [getOrders]);

  const handlePaginate = useCallback((payload: IPaginate) => {
    setPaginate(payload);
    getOrders(filter, payload);
  }, [filter, getOrders]);

  const validateDate = useCallback((startDate: any, endDate: any) => {
    if (!startDate && !endDate) {
      return true;
    }

    if (!startDate || !endDate) {
      return false;
    }

    if (isAfter(new Date(startDate), new Date(endDate))) {
      return false;
    }

    return true;
  }, []);

  const handleSubmit = useCallback(async (payload: any) => {
    try {
      const filter = {
        ...payload,
        id: null,
        page: 1,
        perPage: 10,
        startDate: payload.startDate ? format(payload.startDate, 'yyyy-MM-dd') : null,
        endDate: payload.endDate ? format(payload.endDate, 'yyyy-MM-dd') : null,
        user_id: user ? user.value : null,
        upajs: upajs ? upajs.map((upaj:any) => upaj.value) : null,
        management_id: payload.management_id ? payload.management_id.value : null,
        endSituations: payload.endSituations
          ? payload.endSituations.map((endSituation:any) => endSituation.value)
          : null,
      };

      formRef.current?.setErrors({});

      const schema = Yup.object().shape({
        startDate: Yup.string().ensure()
          .test(
            'is-valid',
            'a Data Início não pode ser maior que a data de Término.',
            () => validateDate(filter.startDate, filter.endDate),
          ),
        endDate: Yup.string().ensure()
          .test(
            'is-valid',
            'a Data Término não pode ser menor que a data de Início.',
            () => validateDate(filter.startDate, filter.endDate),
          ),

      });

      await schema.validate(filter, { abortEarly: false });
      setFilter(filter);

      const { status, data } = await httpGet('closing-forms', { params: filter });

      if (status === 200) {
        setClosingForms(data.data);
        setPaginate(data.pagination);

        if (data.data.length > 0) {
          RefLabelToggle?.current?.click();
        }
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const erros = getValidationError(err);
        formRef.current?.setErrors(erros);
      }
    }
  }, [httpGet, upajs, user, validateDate]);

  const loadOptionsUsers = async (inputValue: any, callback: any):Promise<any> => {
    if (inputValue.length < 3) return;
    const options: any = [];
    const { status, data } = await httpGet(`/users/filter/${inputValue}`, undefined, false);

    if (status === 200) {
      data.forEach((item:any) => {
        options.push({
          value: item.id,
          label: `${item.document} - ${item.name}`,
        });
      });
    }

    callback(options);
  };

  const handleChangeUser = useCallback((event: any) => {
    setUser(event);
  }, []);

  const loadOptionsUpajs = async (inputValue: any, callback: any):Promise<any> => {
    if (inputValue.length < 2) return;
    const options: any = [];
    const { status, data } = await httpGet(`/upajs/filter/${inputValue}`, undefined, false);

    if (status === 200) {
      data.forEach((item:any) => {
        options.push({
          value: item.id,
          label: item.initials,
        });
      });
    }

    callback(options);
  };

  const handleChangeUpajs = useCallback((event: any) => {
    setUpajs(event);
  }, []);

  return (
    <Layout breadcrumbs={breadcrumbs} loading={loading}>
      <Container>
        <HeaderFilterDrawer
          handleSearch={handleSearch}
          placeholder="#protocolo"
        />

        <Drawer>
          <DrawerContent>
            <UForm ref={formRef} onSubmit={handleSubmit}>
              <Grid container spacing={1}>

                <WrapperLabel label="Data Início" xs={12} md={6}>
                  <DatePicker
                    name="startDate"
                    isClearable
                    maxDate={filter?.endDate || new Date()}
                  />
                </WrapperLabel>
                <WrapperLabel label="Data Término" xs={12} md={6}>
                  <DatePicker
                    name="endDate"
                    isClearable
                    maxDate={filter?.endDate || new Date()}
                  />
                </WrapperLabel>

                <WrapperLabel label="Número do registro" xs={12}>
                  <Input name="register_number" />
                </WrapperLabel>

                <WrapperLabel label="UPAJs" xs={12}>
                  <div style={{ width: '100%' }}>
                    <AsyncSelect
                      placeholder="Ex.: 001 DP"
                      noOptionsMessage={() => 'Sem registos'}
                      cacheOptions
                      isClearable
                      isMulti
                      name="upajs"
                      loadOptions={loadOptionsUpajs}
                      onChange={handleChangeUpajs}
                      value={upajs}
                      styles={customStyle}
                    />
                  </div>
                </WrapperLabel>

                <WrapperLabel label="Situação final da ocorrência" xs={12}>
                  <Select
                    name="endSituations"
                    isMulti
                    options={endSituations}
                  />
                </WrapperLabel>

                <WrapperLabel label="Gerência" xs={12}>
                  <Select
                    name="management_id"
                    isClearable
                    options={managements || undefined}
                  />
                </WrapperLabel>

                {iCan('r-closing-form') && (
                  <WrapperLabel label="Agente" xs={12}>
                    <div style={{ width: '100%' }}>
                      <AsyncSelect
                        placeholder="CPF ou Nome"
                        noOptionsMessage={() => 'Sem registos'}
                        cacheOptions
                        isClearable
                        name="user_id"
                        loadOptions={loadOptionsUsers}
                        onChange={handleChangeUser}
                        value={user}
                        styles={customStyle}
                      />
                    </div>
                  </WrapperLabel>
                )}

                <WrapperLabel label="Dinâmica" xs={12}>
                  <Input name="description" />
                </WrapperLabel>
              </Grid>
              <Grid container spacing={1} justify="space-between" alignItems="baseline">

                <Grid item xs={10} md={4}>
                  <span style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <ButtonIcon title="Fechar">
                      <label ref={RefLabelToggle} htmlFor="pure-toggle-right" data-toggle-label="right">
                        <ArrowBarRightIcon />
                      </label>
                    </ButtonIcon>
                    <ButtonIcon
                      title="Limpar"
                      onClick={() => {
                        setUser(null);
                        setUpajs(null);
                      formRef.current?.reset();
                      }}
                    >
                      <TrashIcon />
                    </ButtonIcon>
                  </span>

                </Grid>

                <Grid item xs={12} md={4}>
                  <Button type="submit" isLoading={loading}> Filtrar </Button>
                </Grid>
              </Grid>
            </UForm>
          </DrawerContent>
        </Drawer>

        <Grid container spacing={1}>
          {closingForms && closingForms.map((closingForm: IClosingForm) => (
            <Card
              key={closingForm.id}
              closingForm={closingForm}
            />
          ))}
        </Grid>
        <Pagination paginate={paginate} handlePaginate={handlePaginate} />
      </Container>
    </Layout>
  );
};

export default ClosingForms;
